/* eslint-disable max-lines */
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import Input from '@components/atoms/ui-kit/input';
import Switch from '@components/atoms/ui-kit/switch';
import Button from '@components/atoms/ui-kit/button';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal';
import { Skeleton } from '@components/atoms/ui-kit/skeleton';
import { EmptyState } from '@components/atoms/ui-kit/empty-state';
import {
  useGetSystemSettings,
  useUpdateSystemSetting,
  useCreateSystemSetting,
  useDeleteSystemSetting
} from '@utils/api/back-office/settings';
import {
  SystemSetting,
  parseSettingValue,
  stringifySettingValue
} from '@utils/types/back-office/settings';
import {
  Zap,
  Settings,
  Search,
  Plus,
  Trash2,
  AlertTriangle,
  Check,
  X
} from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '@components/atoms/modals/modal';

// Destructive flags that require confirmation
const DESTRUCTIVE_FLAGS = ['maintenance_mode'];

export default function FlagsSettings() {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [confirmToggle, setConfirmToggle] = useState<{
    setting: SystemSetting;
    newValue: boolean;
  } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<SystemSetting | null>(
    null
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [newFlag, setNewFlag] = useState<{
    key: string;
    value: string;
    value_type: 'string' | 'integer' | 'boolean';
    description: string;
  }>({
    key: '',
    value: 'false',
    value_type: 'boolean',
    description: ''
  });

  const { data, isLoading } = useGetSystemSettings({
    category: 'feature_flag'
  });
  const { mutate: updateSetting, isLoading: isUpdating } =
    useUpdateSystemSetting();
  const { mutate: createSetting, isLoading: isCreating } =
    useCreateSystemSetting();
  const { mutate: deleteSetting } = useDeleteSystemSetting();

  const flags = data?.result || [];

  // Filter flags by search query
  const filteredFlags = useMemo(() => {
    if (!searchQuery) return flags;
    const query = searchQuery.toLowerCase();
    return flags.filter(
      flag =>
        flag.key.toLowerCase().includes(query) ||
        (flag.description?.toLowerCase() || '').includes(query)
    );
  }, [flags, searchQuery]);

  const handleToggle = (setting: SystemSetting) => {
    const currentValue = parseSettingValue(setting);
    if (typeof currentValue !== 'boolean') return;

    const newValue = !currentValue;

    // Check if this is a destructive flag
    if (DESTRUCTIVE_FLAGS.includes(setting.key)) {
      setConfirmToggle({ setting, newValue });
      return;
    }

    executeUpdate(setting, newValue);
  };

  const handleValueChange = (
    setting: SystemSetting,
    newValue: string | number
  ) => {
    updateSetting(
      {
        id: setting.id,
        payload: {
          value: stringifySettingValue(newValue),
          value_type: setting.value_type,
          is_active: setting.is_active
        }
      },
      {
        onSuccess: () => {
          toast.success(t('flag-updated'));
        },
        onError: () => {
          toast.error(t('failed-to-update-flag'));
        }
      }
    );
  };

  const executeUpdate = (settingToUpdate: SystemSetting, value: boolean) => {
    updateSetting(
      {
        id: settingToUpdate.id,
        payload: {
          value: stringifySettingValue(value),
          value_type: settingToUpdate.value_type,
          is_active: settingToUpdate.is_active
        }
      },
      {
        onSuccess: () => {
          toast.success(t('flag-updated'));
        },
        onError: () => {
          toast.error(t('failed-to-update-flag'));
        }
      }
    );
  };

  const handleConfirmDestructive = () => {
    if (confirmToggle) {
      executeUpdate(confirmToggle.setting, confirmToggle.newValue);
      setConfirmToggle(null);
    }
  };

  const handleToggleActive = (setting: SystemSetting) => {
    updateSetting(
      {
        id: setting.id,
        payload: {
          value: setting.value,
          value_type: setting.value_type,
          is_active: !setting.is_active
        }
      },
      {
        onSuccess: () => {
          toast.success(
            setting.is_active ? t('flag-deactivated') : t('flag-activated')
          );
        },
        onError: () => {
          toast.error(t('failed-to-update-flag'));
        }
      }
    );
  };

  const handleDelete = () => {
    if (!confirmDelete) return;
    deleteSetting(confirmDelete.id, {
      onSuccess: () => {
        toast.success(t('flag-deleted'));
        setConfirmDelete(null);
      },
      onError: () => {
        toast.error(t('failed-to-delete-flag'));
      }
    });
  };

  const handleCreateFlag = () => {
    if (!newFlag.key.trim()) {
      toast.error(t('key-is-required'));
      return;
    }

    createSetting(
      {
        key: newFlag.key.trim(),
        value: newFlag.value,
        value_type: newFlag.value_type,
        category: 'feature_flag',
        description: newFlag.description || undefined
      },
      {
        onSuccess: () => {
          toast.success(t('flag-created'));
          setShowAddModal(false);
          setNewFlag({
            key: '',
            value: 'false',
            value_type: 'boolean',
            description: ''
          });
        },
        onError: () => {
          toast.error(t('failed-to-create-flag'));
        }
      }
    );
  };

  const renderFlagValue = (setting: SystemSetting) => {
    const value = parseSettingValue(setting);

    if (setting.value_type === 'boolean') {
      return (
        <Switch
          checked={value as boolean}
          onChange={() => handleToggle(setting)}
          disabled={isUpdating || !setting.is_active}
        />
      );
    }

    if (setting.value_type === 'integer') {
      return (
        <Input
          type="number"
          value={value as number}
          onChange={e => handleValueChange(setting, Number(e.target.value))}
          size="small"
          className="w-24"
          disabled={!setting.is_active}
        />
      );
    }

    return (
      <Input
        type="text"
        value={String(value)}
        onChange={e => handleValueChange(setting, e.target.value)}
        size="small"
        className="w-48"
        disabled={!setting.is_active}
      />
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-4 py-4">
        <Skeleton variant="table" rows={5} />
      </div>
    );
  }

  return (
    <div className="space-y-4 py-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h3
            className="text-sm font-semibold"
            style={{ color: getColour('normalText') }}
          >
            {t('feature-flags')}
          </h3>
          <p
            className="text-xs mt-0.5"
            style={{ color: getColour('inactiveText') }}
          >
            {t('manage-system-wide-feature-flags')}
          </p>
        </div>
        <Button
          label={t('add-flag')}
          variant="primary"
          size="small"
          icon={<Plus className="h-4 w-4" />}
          onClick={() => setShowAddModal(true)}
        />
      </div>

      {/* Search */}
      <div className="relative">
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4"
          style={{ color: getColour('inactiveText') }}
        />
        <input
          type="text"
          placeholder={t('search-flags')}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="w-full pl-9 pr-4 py-2 text-sm rounded-2xl border"
          style={{
            backgroundColor: getColour('secondLayer'),
            borderColor: getColour('border'),
            color: getColour('normalText')
          }}
        />
      </div>

      {/* Flags list */}
      <div
        className="rounded-2xl overflow-hidden divide-y"
        style={{
          backgroundColor: getColour('secondLayer'),
          borderColor: getColour('border')
        }}
      >
        {filteredFlags.map(flag => {
          const isDestructive = DESTRUCTIVE_FLAGS.includes(flag.key);
          return (
            <div
              key={flag.id}
              className="flex items-center justify-between p-3"
              style={{
                borderColor: getColour('border'),
                opacity: flag.is_active ? 1 : 0.6
              }}
            >
              <div className="flex-1 min-w-0 pr-4">
                <div className="flex items-center gap-2">
                  <div className="p-1 rounded-2xl bg-violet-500/10">
                    <Zap className="h-3.5 w-3.5 text-violet-500 dark:text-violet-400" />
                  </div>
                  <p
                    className="text-sm font-mono truncate"
                    style={{ color: getColour('normalText') }}
                    title={flag.key}
                  >
                    {flag.key}
                  </p>
                  {isDestructive && (
                    <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 text-red-500 dark:text-red-400" />
                  )}
                  {!flag.is_active && (
                    <span className="text-xs px-1.5 py-0.5 rounded-2xl bg-zinc-600/10 text-zinc-600 dark:bg-white/5 dark:text-zinc-400">
                      {t('inactive')}
                    </span>
                  )}
                  <span
                    className="text-xs px-1.5 py-0.5 rounded-2xl"
                    style={{
                      backgroundColor: getColour('thirdLayer'),
                      color: getColour('inactiveText')
                    }}
                  >
                    {flag.value_type}
                  </span>
                </div>
                {flag.description && (
                  <p
                    className="text-xs mt-0.5 truncate pl-7"
                    style={{ color: getColour('inactiveText') }}
                    title={flag.description}
                  >
                    {flag.description}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                {renderFlagValue(flag)}
                <button
                  type="button"
                  onClick={() => handleToggleActive(flag)}
                  className="p-1.5 rounded-2xl hover:bg-black/5 dark:hover:bg-white/10"
                  title={flag.is_active ? t('deactivate') : t('activate')}
                >
                  {flag.is_active ? (
                    <Check className="h-3 w-3 text-green-500 dark:text-green-400" />
                  ) : (
                    <X className="h-3 w-3 text-zinc-500 dark:text-zinc-400" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(flag)}
                  className="p-1.5 rounded-2xl hover:bg-red-50 dark:hover:bg-red-950/30"
                  title={t('delete')}
                >
                  <Trash2 className="h-3 w-3 text-red-500 dark:text-red-400" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {filteredFlags.length === 0 && !isLoading && (
        <EmptyState
          icon={<Settings className="h-7 w-7" />}
          title={
            searchQuery ? t('no-flags-match-search') : t('no-flags-configured')
          }
        />
      )}

      {/* Confirmation Modal for destructive flags */}
      <ConfirmationModal
        isOpen={!!confirmToggle}
        onClose={() => setConfirmToggle(null)}
        onConfirm={handleConfirmDestructive}
        title={t('confirm-flag-change')}
        caption={
          confirmToggle
            ? t('confirm-destructive-flag-change', {
                flag: confirmToggle.setting.key,
                value: String(confirmToggle.newValue)
              })
            : ''
        }
        confirmLabel={t('confirm')}
        cancelLabel={t('cancel')}
        destructive
      />

      {/* Delete confirmation modal */}
      <ConfirmationModal
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
        title={t('delete-flag')}
        caption={t('confirm-delete-flag', { flag: confirmDelete?.key || '' })}
        confirmLabel={t('delete')}
        cancelLabel={t('cancel')}
        destructive
      />

      {/* Add flag modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={t('add-feature-flag')}
        size="small"
        footerActions={[
          <Button
            key="cancel"
            label={t('cancel')}
            variant="secondary"
            onClick={() => setShowAddModal(false)}
          />,
          <Button
            key="create"
            label={isCreating ? t('creating') : t('create')}
            onClick={handleCreateFlag}
            disabled={isCreating || !newFlag.key.trim()}
          />
        ]}
      >
        <div className="space-y-4">
          <Input
            label={t('key')}
            value={newFlag.key}
            onChange={e =>
              setNewFlag(prev => ({ ...prev, key: e.target.value }))
            }
            placeholder="enable_feature_x"
            required
          />
          <div>
            <label
              className="text-xs mb-1 block"
              style={{ color: getColour('inactiveText') }}
            >
              {t('value-type')}
            </label>
            <select
              value={newFlag.value_type}
              onChange={e => {
                const type = e.target.value as 'boolean' | 'string' | 'integer';
                setNewFlag(prev => ({
                  ...prev,
                  value_type: type,
                  value:
                    type === 'boolean' ? 'false' : type === 'integer' ? '0' : ''
                }));
              }}
              className="w-full px-3 py-2 text-sm rounded-2xl border"
              style={{
                backgroundColor: getColour('secondLayer'),
                borderColor: getColour('border'),
                color: getColour('normalText')
              }}
            >
              <option value="boolean">Boolean</option>
              <option value="string">String</option>
              <option value="integer">Integer</option>
            </select>
          </div>
          {newFlag.value_type === 'boolean' ? (
            <div className="flex items-center justify-between">
              <label style={{ color: getColour('normalText') }}>
                {t('default-value')}
              </label>
              <Switch
                checked={newFlag.value === 'true'}
                onChange={e =>
                  setNewFlag(prev => ({
                    ...prev,
                    value: e.target.checked ? 'true' : 'false'
                  }))
                }
              />
            </div>
          ) : (
            <Input
              label={t('default-value')}
              type={newFlag.value_type === 'integer' ? 'number' : 'text'}
              value={newFlag.value}
              onChange={e =>
                setNewFlag(prev => ({ ...prev, value: e.target.value }))
              }
            />
          )}
          <Input
            label={t('description')}
            value={newFlag.description}
            onChange={e =>
              setNewFlag(prev => ({ ...prev, description: e.target.value }))
            }
            placeholder={t('optional-description')}
          />
        </div>
      </Modal>
    </div>
  );
}
